import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar } from 'recharts';
import FilterBar, { EMPTY_FILTERS } from '../components/FilterBar';
import { getMonthlyEnrolment, getEnrolmentByFacility, getEnrolmentByRegion } from '../utils/api';

const TT = { backgroundColor:'#111827', border:'1px solid rgba(0,191,165,0.2)', borderRadius:8 };

export default function EnrolmentPage() {
  const [monthly,    setMonthly]    = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [regions,    setRegions]    = useState([]);
  const [filters,    setFilters]    = useState(EMPTY_FILTERS);
  const [loading,    setLoading]    = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, f, r] = await Promise.all([
        getMonthlyEnrolment(filters),
        getEnrolmentByFacility(filters),
        getEnrolmentByRegion(filters),
      ]);
      setMonthly(m.data);
      setFacilities(f.data);
      setRegions(r.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const maxFacility = facilities[0]?.total || 1;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">Patient Enrolment</Typography>
          <Typography variant="body2" color="text.secondary">Monthly trends, regions and facility breakdown</Typography>
        </Box>
      </Box>

      <FilterBar filters={filters} onChange={setFilters} />

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Cumulative Enrolment Trend</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={monthly}>
                  <defs>
                    <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00BFA5" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00BFA5" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                  <XAxis dataKey="month" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
                  <YAxis tick={{ fontSize:11, fill:'#90A4AE' }}/>
                  <Tooltip contentStyle={TT}/>
                  <Legend/>
                  <Area type="monotone" dataKey="cumulative" name="Total Enrolled" stroke="#00BFA5" strokeWidth={2.5} fill="url(#cumGrad)"/>
                  <Area type="monotone" dataKey="enrolled" name="Monthly New" stroke="#FFB300" strokeWidth={2} fill="none"/>
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height:'100%' }}>
            <CardContent>
              <Typography variant="h6" mb={2}>By Region</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={regions} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false}/>
                  <XAxis type="number" tick={{ fontSize:10, fill:'#90A4AE' }}/>
                  <YAxis dataKey="region" type="category" width={80} tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
                  <Tooltip contentStyle={TT}/>
                  <Bar dataKey="mtn" name="MTN" stackId="a" fill="#FFB300"/>
                  <Bar dataKey="em" name="Eswatini Mobile" stackId="a" fill="#00BFA5" radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>Enrolment by Facility</Typography>
          {loading && <LinearProgress sx={{ mb:1 }}/>}
          <TableContainer sx={{ maxHeight:440 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Facility</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">MTN</TableCell>
                  <TableCell align="right">EM</TableCell>
                  <TableCell align="right">Active</TableCell>
                  <TableCell sx={{ minWidth:120 }}>Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facilities.map((row, i) => (
                  <TableRow key={i} hover sx={{ '&:hover':{ bgcolor:'rgba(0,191,165,0.04)' } }}>
                    <TableCell sx={{ fontWeight:500 }}>{row.facility}</TableCell>
                    <TableCell>
                      <Chip label={row.region} size="small" sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.65rem', bgcolor:'rgba(0,191,165,0.1)', color:'primary.main', height:20 }}/>
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily:'"IBM Plex Mono",monospace', fontWeight:700, color:'primary.main' }}>{row.total}</TableCell>
                    <TableCell align="right" sx={{ fontFamily:'"IBM Plex Mono",monospace', color:'#FFB300' }}>{row.mtn || 0}</TableCell>
                    <TableCell align="right" sx={{ fontFamily:'"IBM Plex Mono",monospace', color:'#00BFA5' }}>{row.em || 0}</TableCell>
                    <TableCell align="right" sx={{ fontFamily:'"IBM Plex Mono",monospace', color:'#69F0AE' }}>{row.active || 0}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress variant="determinate" value={Math.min((row.total/maxFacility)*100,100)}
                          sx={{ flex:1, height:5, borderRadius:3, bgcolor:'rgba(255,255,255,0.06)', '& .MuiLinearProgress-bar':{ bgcolor:'#00BFA5', borderRadius:3 } }}/>
                        <Typography variant="caption" sx={{ fontFamily:'"IBM Plex Mono",monospace', minWidth:30, color:'text.secondary' }}>
                          {((row.total/maxFacility)*100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
